/*
 * Journey step: review and purchase.
 *
 * Collects every prior step into one cart add. Nothing is written until the
 * customer presses the button, so re-entering the step does not duplicate the
 * cart.
 *
 * Each item goes into the cart as its own normal line, so the customer can
 * change quantities, add or remove pieces individually. We deliberately do NOT
 * use the `_bundle_` line item property here: that key triggers the theme's
 * bundle-grouping, which collapses the whole build into a single uneditable
 * "Build a Kit" line.
 *
 * For reporting, every line carries one hidden tracking property
 * (`_kit_source`, value from the section, default "DIY Bundle"). It is a line
 * item property, not an order-level custom attribute, because order attributes
 * broke the Cin7 and 3PL sync earlier this year. The leading underscore hides
 * it from the customer while keeping it on the order line for reporting, and a
 * Shopify Flow can promote it to a real order tag on order creation.
 */
(function () {
  'use strict';

  function Review(root) {
    this.root = root;
    this.config = this.readConfig();
    if (!this.config) return;

    this.stepId = this.config.stepId || 'review';
    this.steps = this.config.steps || {};
    this.labels = this.config.labels || {};

    this.groupsEl = root.querySelector('[data-rev-groups]');
    this.emptyEl = root.querySelector('[data-rev-empty]');
    this.summaryEl = root.querySelector('[data-rev-summary]');
    this.totalEl = root.querySelector('[data-rev-total]');
    this.buttonEl = root.querySelector('[data-rev-checkout]');
    this.buttonLabelEl = root.querySelector('[data-rev-checkout-label]');
    this.errorEl = root.querySelector('[data-rev-error]');

    this.journey = null;
    this.submitting = false;

    this.money = this.moneyFormatter();
    this.bind();
  }

  Review.prototype.readConfig = function () {
    var el = this.root.querySelector('[data-rev-config]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      return null;
    }
  };

  Review.prototype.moneyFormatter = function () {
    var currency = this.config.currency || 'AUD';
    try {
      var fmt = new Intl.NumberFormat(undefined, { style: 'currency', currency: currency });
      return function (cents) {
        return fmt.format(cents / 100);
      };
    } catch (err) {
      return function (cents) {
        return '$' + (cents / 100).toFixed(2);
      };
    }
  };

  Review.prototype.bind = function () {
    var self = this;

    if (window.CatnetsJourney) {
      this.attach(window.CatnetsJourney);
    } else {
      document.addEventListener('journey:ready', function (e) {
        self.attach(e.detail.journey);
      });
    }

    document.addEventListener('journey:step:enter', function (e) {
      if (e.detail.step === self.stepId) self.render();
    });

    // Any earlier change should refresh the review while it is open.
    document.addEventListener('journey:step:complete', function () {
      self.render();
    });

    if (this.buttonEl) {
      this.buttonEl.addEventListener('click', function () {
        self.checkout();
      });
    }
  };

  Review.prototype.attach = function (journey) {
    this.journey = journey;
    this.render();
  };

  Review.prototype.data = function (key) {
    var id = this.steps[key] || key;
    return this.journey ? this.journey.data(id) : null;
  };

  /**
   * The complete build, as display groups. Also the source of the cart lines,
   * so what the customer sees is exactly what gets added.
   */
  Review.prototype.build = function () {
    var groups = [];
    var lines = [];

    // Ends wider than the main run get their own net, so the netting step can
    // hand over several nets. Older saved journeys carry a single net at the
    // top level instead of a nets array.
    var netting = this.data('netting');
    var nets = [];
    if (netting) {
      if (netting.nets && netting.nets.length) nets = netting.nets;
      else if (netting.variantId) nets = [netting];
    }

    if (nets.length) {
      var rows = [];

      for (var n = 0; n < nets.length; n++) {
        var net = nets[n];
        var colourLabel = net.colourLabel || netting.colourLabel;
        var title = net.productTitle + ' - ' + net.variantTitle +
          (colourLabel ? ' (' + colourLabel + ')' : '');

        // With more than one net, say which part of the build each is for.
        if (nets.length > 1 && net.groupLabel) title += ' - ' + net.groupLabel;

        rows.push({
          title: title,
          quantity: net.quantity,
          total: net.totalPrice,

          // By the metre, "Qty 10" hides the size that matters. Show the cut:
          // metres of run by the width, e.g. "10m x 3m". Rolls carry their
          // size in the variant title already.
          qtyText: net.format === 'btm'
            ? net.quantity + 'm x ' + net.variantTitle
            : null
        });
        lines.push({ id: net.variantId, quantity: net.quantity });
      }

      groups.push({
        key: 'netting',
        label: this.labels.netting || 'Your netting',
        rows: rows
      });

      if (netting.zip && netting.zip.variantId) {
        var zipQty = netting.zip.quantity || 1;
        groups.push({
          key: 'zip',
          label: this.labels.zip || 'Your zip',
          rows: [{ title: netting.zip.title, quantity: zipQty, total: netting.zip.price * zipQty }]
        });
        lines.push({ id: netting.zip.variantId, quantity: zipQty });
      }
    }

    var hardware = this.data('hardware');
    if (hardware && hardware.items && hardware.items.length) {
      groups.push({
        key: 'hardware',
        label: this.labels.hardware || 'Your hardware',
        rows: hardware.items.map(function (i) {
          return { title: i.title, quantity: i.quantity, total: i.total };
        })
      });
      for (var h = 0; h < hardware.items.length; h++) {
        lines.push({ id: hardware.items[h].variantId, quantity: hardware.items[h].quantity });
      }
    }

    var skywalks = this.data('skywalks');
    if (skywalks && skywalks.items && skywalks.items.length) {
      groups.push({
        key: 'skywalks',
        label: this.labels.skywalks || 'Your Skywalks',
        rows: skywalks.items.map(function (i) {
          return { title: i.title, quantity: i.quantity, total: i.total };
        })
      });
      for (var sk = 0; sk < skywalks.items.length; sk++) {
        lines.push({ id: skywalks.items[sk].variantId, quantity: skywalks.items[sk].quantity });
      }
    }

    var total = 0;
    for (var g = 0; g < groups.length; g++) {
      for (var r = 0; r < groups[g].rows.length; r++) total += groups[g].rows[r].total;
    }

    return { groups: groups, lines: lines, total: total };
  };

  Review.prototype.render = function () {
    if (!this.journey || !this.groupsEl) return;

    var build = this.build();

    if (!build.lines.length) {
      this.groupsEl.innerHTML = '';
      if (this.emptyEl) this.emptyEl.hidden = false;
      if (this.summaryEl) this.summaryEl.hidden = true;
      return;
    }

    if (this.emptyEl) this.emptyEl.hidden = true;
    if (this.summaryEl) this.summaryEl.hidden = false;

    var qtyLabel = this.labels.qty || 'Qty';
    var html = '';

    for (var i = 0; i < build.groups.length; i++) {
      var group = build.groups[i];
      html += '<div class="jrev__group"><h4 class="jrev__group-title">' + group.label + '</h4>';

      for (var r = 0; r < group.rows.length; r++) {
        var row = group.rows[r];
        html +=
          '<div class="jrev__row">' +
            '<span class="jrev__row-title">' + row.title + '</span>' +
            '<span class="jrev__row-qty">' + (row.qtyText || qtyLabel + ' ' + row.quantity) + '</span>' +
            '<span class="jrev__row-total">' + this.money(row.total) + '</span>' +
          '</div>';
      }

      html += '</div>';
    }

    this.groupsEl.innerHTML = html;
    if (this.totalEl) this.totalEl.textContent = this.money(build.total);
  };

  Review.prototype.setError = function (message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || '';
    this.errorEl.hidden = !message;
  };

  Review.prototype.checkout = function () {
    if (this.submitting || !this.journey) return;

    var build = this.build();
    if (!build.lines.length) return;

    this.submitting = true;
    this.setError('');
    if (this.buttonEl) this.buttonEl.disabled = true;
    if (this.buttonLabelEl) this.buttonLabelEl.textContent = this.labels.adding || 'Adding...';

    // One hidden tracking tag per line, so orders placed through the journey are
    // reportable without collapsing the lines into a single bundle.
    var tag = this.config.bundleTag || 'DIY Bundle';

    var payload = {
      items: build.lines.map(function (line) {
        return {
          id: line.id,
          quantity: line.quantity,
          properties: { _kit_source: tag }
        };
      })
    };

    var self = this;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (response) {
        if (!response.ok) throw new Error('cart add failed: ' + response.status);
        return response.json();
      })
      .then(function () {
        // Announce the successful add for analytics before leaving the page.
        try {
          document.dispatchEvent(new CustomEvent('journey:cart:added', {
            detail: { value: build.total, itemCount: build.lines.length },
            bubbles: true
          }));
        } catch (err) { /* tracking must never block checkout */ }

        var url = self.config.checkoutUrl || '/cart';
        if (self.config.campaign) {
          url += (url.indexOf('?') > -1 ? '&' : '?') + 'utm_campaign=' + encodeURIComponent(self.config.campaign);
        }
        self.go(url);
      })
      .catch(function () {
        self.submitting = false;
        if (self.buttonEl) self.buttonEl.disabled = false;
        if (self.buttonLabelEl) self.buttonLabelEl.textContent = self.labels.checkout || 'Add everything to cart';
        self.setError(self.labels.error || 'Something went wrong. Please try again.');
      });
  };

  // Single seam for the post-add redirect, so it can be observed in tests where
  // a real navigation is not possible.
  Review.prototype.go = function (url) {
    window.location.href = url;
  };

  function boot() {
    var roots = document.querySelectorAll('[data-journey-step-source]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].querySelector('[data-rev-config]')) {
        window.CatnetsReview = new Review(roots[i]);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
